import React, { useEffect, useState, useRef } from 'react';
import { trackContactSubmitClick } from '../../analytics/pageAnalytics';
// import logo from '../../../public/asset/logo.png';

const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [active, setActive] = useState("home");
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        number: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });
    const [showNotification, setShowNotification] = useState(false);
    const [notificationData, setNotificationData] = useState({ type: 'success', message: '' });
    const scrollPositionRef = useRef(0);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleScrollActive = () => {
            const scrollPosition = window.scrollY;
            const offset = 150; // Offset from top to trigger section change
            
            // Get all sections
            const aboutSection = document.getElementById('about');
            const faqsSection = document.getElementById('faqs');

            // Collect all sections with their positions
            const sections = [];
            
            if (faqsSection) {
                sections.push({ id: 'faqs', top: faqsSection.offsetTop, element: faqsSection });
            }
            
            if (aboutSection) {
                sections.push({ id: 'about', top: aboutSection.offsetTop, element: aboutSection });
            }

            // Sort sections by their position (top to bottom)
            sections.sort((a, b) => a.top - b.top);

            // Check if we're at the very top (home section)
            if (scrollPosition < offset) {
                setActive("home");
                return;
            }

            // Check sections from bottom to top to find which one we're in
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (scrollPosition + offset >= section.top) {
                    setActive(section.id);
                    return;
                }
            }

            // If we haven't reached any section yet, we're in home
            if (sections.length > 0 && scrollPosition < sections[0].top - offset) {
                setActive("home");
            }
        };

        // Initial check
        handleScrollActive();

        // Add scroll listener with throttling for better performance
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScrollActive();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const restoreScrollPosition = () => {
        const savedScrollY = scrollPositionRef.current;
        
        // Temporarily disable smooth scrolling globally
        const htmlStyle = document.documentElement.style.scrollBehavior;
        const bodyStyle = document.body.style.scrollBehavior;
        const rootStyle = document.getElementById('root')?.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';
        if (document.getElementById('root')) {
            document.getElementById('root').style.scrollBehavior = 'auto';
        }
        
        // Restore body styles
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.left = '';
        document.body.style.right = '';
        
        // Set scroll position immediately using direct assignment (no animation)
        // Do this synchronously before any frame renders
        document.documentElement.scrollTop = savedScrollY;
        document.body.scrollTop = savedScrollY;
        
        // Also use scrollTo with auto behavior as additional measure
        window.scrollTo({
            top: savedScrollY,
            left: 0,
            behavior: 'auto'
        });
        
        // Restore original scroll behavior after ensuring scroll is set
        requestAnimationFrame(() => {
            document.documentElement.style.scrollBehavior = htmlStyle || '';
            document.body.style.scrollBehavior = bodyStyle || '';
            if (document.getElementById('root')) {
                document.getElementById('root').style.scrollBehavior = rootStyle || '';
            }
        });
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isContactModalOpen) {
                restoreScrollPosition();
                setIsContactModalOpen(false);
            }
        };

        if (isContactModalOpen) {
            // Save current scroll position
            scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop || 0;
            
            // Disable scrolling on body and html
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            // Prevent page jump by maintaining scroll position
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPositionRef.current}px`;
            document.body.style.width = '100%';
            document.body.style.left = '0';
            document.body.style.right = '0';
            
            window.addEventListener('keydown', handleKeyDown);
            
            // Cleanup function
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isContactModalOpen]);

    const openContactModal = () => {
        setIsContactModalOpen(true);
        setIsMobileMenuOpen(false);
    };

    const closeContactModal = () => {
        restoreScrollPosition();
        setIsContactModalOpen(false);
        setFormData({
            fullName: '',
            email: '',
            number: '',
            message: ''
        });
        setSubmitStatus({ type: null, message: '' });
        setShowNotification(false);
    };

    const closeNotification = () => {
        setShowNotification(false);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });
        trackContactSubmitClick();

        try {
            const response = await fetch('https://devapi.car-hangout.com/api/v2/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({
                    name: formData.fullName.trim(),
                    email: formData.email.trim(),
                    phone: formData.number.trim(),
                    message: formData.message.trim()
                })
            });

            let data = {};
            try {
                data = await response.json();
            } catch (jsonError) {
                // If response is not JSON, continue with empty data object
            }

            if (!response.ok) {
                // Extract error message from API response
                const errorMessage = data?.message || data?.error || data?.errors?.join(', ') || `Failed to submit contact request. (Status: ${response.status})`;
                throw new Error(errorMessage);
            }

            setFormData({ fullName: '', email: '', number: '', message: '' });
            setSubmitStatus({ type: null, message: '' });
            // Close the modal and show success notification
            setIsContactModalOpen(false);
            setNotificationData({
                type: 'success',
                message: 'Thank you! Our team will reach out shortly.'
            });
            setShowNotification(true);
            // Auto-close notification after 5 seconds
            setTimeout(() => {
                setShowNotification(false);
            }, 5000);
        } catch (error) {
            // Show error notification popup
            const errorMessage = error.message || 'Something went wrong. Please try again later.';
            setNotificationData({
                type: 'error',
                message: errorMessage
            });
            // Clear form data
            setFormData({ fullName: '', email: '', number: '', message: '' });
            setSubmitStatus({ type: null, message: '' });
            // Close the modal and show error notification
            setIsContactModalOpen(false);
            setShowNotification(true);
            // Auto-close error notification after 5 seconds
            setTimeout(() => {
                setShowNotification(false);
            }, 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClick = (event) => {
        event.stopPropagation();
    };

    return (
        <>
            <style>
                {`
                    @keyframes headerSlideFromTop {
                        0% {
                            opacity: 0;
                            transform: translateY(-100px);
                            background: transparent;
                        }
                        70% {
                            opacity: 1;
                            transform: translateY(0);
                            background: transparent;
                        }
                        100% {
                            opacity: 1;
                            transform: translateY(0);
                            background: linear-gradient(135deg, rgba(15, 15, 16, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%);
                        }
                    }
                    @keyframes slideInRight {
                        0% {
                            opacity: 0;
                            transform: translateX(100%);
                        }
                        100% {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                `}
            </style>
            <style>
                {`
                    @media (max-width: 319px) {
                        .mobile-popup-modal {
                            max-width: 95vw !important;
                            max-height: 80vh !important;
                            overflow-y: auto !important;
                        }
                    }
                `}
            </style>
            <div
                className="fixed inset-x-0 top-0 xxs:h-[10%] xs:h-[10%] sm:h-[9%] md:h-[5%] lg:h-[10%] xl:h-[10%] 2xl:h-[10%] pointer-events-none z-[45] transition-opacity duration-300"
                style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    opacity: isScrolled ? 1 : 0
                }}
            ></div>
            <header 
                className={`fixed z-50 top-4 left-4 right-4 md:top-3 md:left-3 md:right-3 w-auto md:w-auto h-auto md:h-20 rounded-2xl px-2 md:px-5 py-3 md:py-4 max-w-9xl mx-auto transition-all duration-300 ${
                    isScrolled ? 'bg-[rgba(0,0,0,0.5)] backdrop-blur-[10px] backdrop-saturate-150 shadow-[0_12px_28px_rgba(0,0,0,0.45)]' : 'bg-transparent shadow-none'
                }`}
                style={{
                    WebkitBackdropFilter: isScrolled ? 'blur(10px)' : 'none',
                    animation: 'headerSlideFromTop 1.5s ease-out 0.1s both',
                    animationFillMode: 'both'
                }}
            >
            <div className="flex justify-between items-center h-full">
                <div className="flex items-center gap-2 md:gap-2 lg:gap-4 text-white flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-shrink-0 px-2">
                        <img src="/asset/MainLogo.png" alt="Hangout" className="w-8 md:w-10 lg:w-11 xl:w-11 h-8 md:h-10 lg:h-11 xl:h-11 rounded-lg" />
                        <span className="font-noto-sans font-bold text-lg md:text-xl lg:text-2xl xl:text-2xl leading-none tracking-normal text-white capitalize flex items-center ml-1 md:ml-2 whitespace-nowrap">
                           Car Hangout
                        </span>
                    </div>
                    <div 
                        className="w-px h-5 border border-white hidden md:block flex-shrink-0 mx-1 lg:mx-3"
                        style={{ opacity: 0.05 }}
                    ></div>
                    <nav className="hidden md:flex items-center space-x-3 lg:space-x-8 xl:space-x-14 flex-shrink-0">
                        <a 
                            href="#home" 
                            className={`${active === "home" ? "text-white" : "text-gray-200"} hover:text-white transition-colors duration-150 font-noto-sans font-regular text-sm lg:text-base xl:text-base leading-none tracking-normal capitalize no-underline whitespace-nowrap`}
                            onClick={(e) => {
                                e.preventDefault();
                                setActive("home");
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            Home
                        </a>
                        <a 
                            href="#about" 
                            className={`${active === "about" ? "text-white" : "text-gray-200"} hover:text-white transition-colors duration-150 font-noto-sans font-regular text-sm lg:text-base xl:text-base leading-none tracking-normal capitalize no-underline whitespace-nowrap`}
                            onClick={(e) => {
                                e.preventDefault();
                                setActive("about");
                                const element = document.getElementById('about');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }}
                        >
                            About
                        </a>
                        <a 
                            href="#faqs" 
                            className={`${active === "faqs" ? "text-white" : "text-gray-200"} hover:text-white transition-colors duration-150 font-noto-sans font-regular text-sm lg:text-base xl:text-base leading-none tracking-normal capitalize no-underline whitespace-nowrap`}
                            onClick={(e) => {
                                e.preventDefault();
                                setActive("faqs");
                                const element = document.getElementById('faqs');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }}
                        >
                            FAQ's
                        </a>
                    </nav>
                </div>
                <div className="hidden md:flex items-center flex-shrink-0 px-0 lg:px-2">
                    
                    <button 
                        className="text-white font-noto-sans font-semibold text-sm lg:text-base xl:text-lg leading-none capitalize hover:bg-orange-600 transition-colors duration-300 rounded-2xl flex items-center justify-center w-[100px] h-[40px] lg:w-[140px] lg:h-[48px] xl:w-[156px] xl:h-[50px]"
                        style={{
                            borderRadius: '15px',
                            background: '#F44C1D'
                        }}
                        onClick={openContactModal}
                    >
                        Contact Us
                    </button>
                </div>
                
                {/* Mobile Menu Button */}
                <button 
                    className="md:hidden text-white p-2 relative"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                    </svg>
                    
                    {/* Mobile Dropdown Menu */}
                    {isMobileMenuOpen && (
                        <div 
                            className="fixed top-20 left-1/2 z-50 w-[min(420px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl bg-[#0F0F10] shadow-xl"
                            style={{
                                padding: '20px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                          
                            <nav className="flex flex-col text-left">
                                <a 
                                    href="#home" 
                                    className={`py-3 text-left text-[15px] font-semibold ${active === "home" ? "text-white" : "text-gray-200"} no-underline hover:text-white border-b border-gray-100/10 last:border-b-0 transition-colors duration-150`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActive("home");
                                        setIsMobileMenuOpen(false);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                >
                                    <span>Home</span>
                                </a>
                                <a 
                                    href="#about" 
                                    className={`py-3 text-left text-[15px] font-semibold ${active === "about" ? "text-white" : "text-gray-200"} no-underline hover:text-white border-b border-gray-100/10 last:border-b-0 transition-colors duration-150`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActive("about");
                                        setIsMobileMenuOpen(false);
                                        const element = document.getElementById('about');
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }}
                                >
                                    <span>About</span>
                                </a>
                                <a 
                                    href="#faqs" 
                                    className={`py-3 text-left text-[15px] font-semibold ${active === "faqs" ? "text-white" : "text-gray-200"} no-underline hover:text-white border-b border-gray-100/10 last:border-b-0 transition-colors duration-150`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActive("faqs");
                                        setIsMobileMenuOpen(false);
                                        const element = document.getElementById('faqs');
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }}
                                >
                                    <span>FAQ's</span>
                                </a>
                                <a 
                                    href="#contact" 
                                    className={`py-3 text-left text-[15px] font-semibold ${active === "contact" ? "text-white" : "text-gray-200"} no-underline hover:text-white border-b border-gray-100/10 last:border-b-0 transition-colors duration-150`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActive("contact");
                                        openContactModal();
                                    }}
                                >
                                    <span>Contact Us</span>
                                </a>
                            </nav>
                        </div>
                    )}
                </button>
            </div>
        </header>

        {/* Success/Error Notification Popup */}
        {showNotification && (
            <div 
                className="fixed top-4 right-4 z-[80] animate-slide-in-right"
                style={{
                    animation: 'slideInRight 0.3s ease-out'
                }}
            >
                <div className={`bg-[#0F0F10] border ${notificationData.type === 'success' ? 'border-white/20' : 'border-red-500/30'} rounded-xl shadow-[0_10px_36px_rgba(0,0,0,0.45)] p-4 min-w-[300px] max-w-[400px] flex items-start gap-3`}>
                    <div className="flex-shrink-0 mt-0.5">
                        {notificationData.type === 'success' ? (
                            <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>
                    <div className="flex-1">
                        <h3 className={`font-noto-sans font-semibold text-base mb-1 ${notificationData.type === 'success' ? 'text-white' : 'text-red-400'}`}>
                            {notificationData.type === 'success' ? 'Success!' : 'Error!'}
                        </h3>
                        <p className={`font-noto-sans text-sm ${notificationData.type === 'success' ? 'text-gray-300' : 'text-red-300'}`}>
                            {notificationData.message}
                        </p>
                    </div>
                    <button
                        onClick={closeNotification}
                        className="flex-shrink-0 text-white/60 hover:text-white transition-colors"
                        aria-label="Close notification"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        )}

        {isContactModalOpen && (
            <div 
                className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6 py-10 bg-black/70 backdrop-blur-sm overflow-y-auto"
                onClick={closeContactModal}
            >
                <div 
                    className="relative w-full max-w-[460px] sm:max-w-[330px] md:max-w-[460px] max-h-[90vh] rounded-[20px] bg-[#0F0F10] text-white shadow-[0_10px_36px_rgba(0,0,0,0.45)] overflow-y-auto mobile-popup-modal"
                    onClick={handleModalClick}
                >
                    <div className="relative px-8 xxs:px-6 sm:px-10 pt-16 pb-12 text-center">
                        <div 
                            className="absolute inset-0"
                            style={{
                                background: 'linear-gradient(180deg, rgba(22,22,26,0.85) 0%, rgba(15,15,16,0.95) 100%)',
                                backdropFilter: 'blur(6px)',
                             
                            }}
                        ></div>
                        <div 
                            className="absolute inset-0 opacity-[0.18]"
                            style={{
                                backgroundImage: `
                                    linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px),
                                    linear-gradient(0deg, rgba(255,255,255,0.12) 1px, transparent 1px)
                                `,
                                backgroundSize: '36px 36px, 36px 36px',
                                backgroundPosition: 'center'
                            }}
                        ></div>
                        {/* <div 
                            className="absolute inset-0"
                            style={{
                                background: 'radial-gradient(circle at top, rgba(255,255,255,0.26), transparent 55%)'
                            }}
                        ></div> */}
                        <div className="relative mx-auto mb-3 w-12 h-12 sm:mb-8 sm:w-16 sm:h-16 md:mb-9 md:w-20 md:h-20">
                            <div 
                                className="absolute inset-0 rounded-[20px] border border-white/15"
                                style={{
                                    background: 'linear-gradient(180deg, rgba(36,36,40,0.85) 0%, rgba(12,12,14,0.95) 100%)',
                                    boxShadow: '0px 9px 28px rgba(255,255,255,0.60)',
                             
                                }}
                            ></div>
                            <div 
                                className="absolute inset-[8px] rounded-[10px] bg-transparent border border-white/20 flex items-center justify-center"
                                style={{
                                    boxShadow: 'inset 0 0 0px rgba(255,255,255,0.18)',
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.5a2.25 2.25 0 01-2.26 0l-7.5-4.5a2.25 2.25 0 01-1.07-1.916V6.75" />
                            </svg>
                            </div>
                        </div>
                        {/* <p className="relative text-[12px] uppercase tracking-[.35em] text-white/45 font-semibold">
                            Get In
                        </p> */}
                        <h2 className="relative text-lg font-extrabold tracking-wide mt-4  sm:text-2xl md:text-3xl">
                            <span className="text-white text-3xl font-noto-sans font-bold" style={{ transform: 'scaleY(1.3)', lineHeight: '1.1', display: 'inline-block' }}>GET IN</span>
                            <span className="text-[#F44C1D] text-3xl font-noto-sans font-bold"  style={{ transform: 'scaleX(1.1) scaleY(1.3)', lineHeight: '1.1', display: 'inline-block', marginLeft: '12px' }}>TOUCH</span>
                        </h2>
                        <p className="relative mt-3 -mb-14 text-sm text-gray-200  font-noto-sans leading-relaxed">
                            Have a question or need assistance? Contact our support team for professional guidance and timely responses.
                        </p>
                    </div>
                    <div className="px-8 py-4 sm:px-5 sm:py-6 md:px-10 md:py-8 mt-1">
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <div>
                                <label className="text-xs font-semibold font-noto-sans tracking-wide text-white/50 mb-2 block mt-0 sm:text-sm">
                                    Full Name<span className="text-[#F44C1D]">*</span>
                                </label>
                                <input 
                                    type="text"
                                    name="fullName"
                                    placeholder=" Full Name"
                                    className="w-full rounded-xl bg-[#1F1F1F] px-3 py-2  md:py-4 text-xs text-white placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F44C1D] sm:px-4 sm:py-3 sm:text-sm"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-semibold font-noto-sans tracking-wide text-white/50 mb-2 block sm:text-sm">
                                        Email<span className="text-[#F44C1D]">*</span>
                                    </label>
                                    <input 
                                        type="email"
                                        name="email"
                                        placeholder=" Email Address"
                                        className="w-full rounded-xl bg-[#1F1F1F] px-3 py-2 text-xs text-white placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F44C1D] sm:px-4 sm:py-3 sm:text-sm"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold font-noto-sans text-white/50  mb-2 block">
                                        Number<span className="text-[#F44C1D]">*</span>
                                    </label>
                                    <input 
                                        type="tel"
                                        name="number"
                                        placeholder=" Contact Number"
                                        className="w-full rounded-xl bg-[#1F1F1F] pl-4 pr-1.2 py-2 text-xs text-white placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F44C1D] sm:px-4 sm:py-3 sm:text-sm"
                                        value={formData.number}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold font-noto-sans text-white/50 mb-2 block">
                                    Message
                                </label>
                                <textarea
                                    rows="4"
                                    name="message"
                                    placeholder="Type your message or inquiry here"
                                    className="w-full rounded-xl bg-[#1F1F1F] px-3 sm:py-3  font-noto-sans xxs:py-1 text-xs text-white placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F44C1D] resize-none sm:px-4 sm:py-3 sm:text-sm"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>
                            {submitStatus.message && submitStatus.type === 'error' && (
                                <div className="text-sm font-noto-sans font-semibold text-red-400">
                                    {submitStatus.message}
                                </div>
                            )}
                            <div className="flex gap-3 -pt-1 sm:pt-1 font-noto-sans sm:gap-4 sm:pt-2">
                                <button
                                    type="button"
                                    className="w-full xxs:h-10 xs:h-10 sm:h-10  rounded-[8px] bg-[#2B2B2D] text-white font-semibold font-noto-sans text-xs leading-none flex items-center justify-center transition hover:bg-[#353537] sm:h-12 sm:text-sm"
                                    onClick={closeContactModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="w-full xxs:h-10  xs:h-10   sm:h-10  rounded-[8px] bg-[#F44C1D] text-white font-noto-sans font-semibold text-xs leading-none flex items-center justify-center transition hover:bg-[#ff5e2f] shadow-[0_6px_20px_rgba(244,76,29,0.35)] disabled:opacity-60 disabled:cursor-not-allowed sm:h-12 sm:text-sm"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                        <button
                            type="button"
                            className="absolute top-3 right-3  text-white/40 hover:text-white/70 font-noto-sans transition sm:top-6 sm:right-6"
                            onClick={closeContactModal}
                            aria-label="Close contact form"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
     );
};

export default Header;
